import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { ConfirmDialog } from '../../components/Dialog';
import { fmtDate, MONTHS } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { ChipRadios } from '../ChipRadios';
import { useLeistungen } from '../LeistungDaten';
import { PdfDialog } from '../PdfDialog';
import { darf, GesperrtKnopf } from '../tarifHinweis';
import type { Overview } from '../types';
import { urlId } from '../urlId';
import { AbrechnenDialog } from './AbrechnenDialog';
import { stundenText } from './dauer';
import { FremdeUhr, serverZeitMerken, UhrKarte, UhrStarten } from './Uhr';
import { KundeSuche, loeschenFrage, ZeitDialog } from './ZeitDialog';
import { istOffen, summeMinuten, zeitenCsvUrl, zeitenUrl, zeitUrl, type Uhr, type Zeit, type ZeitFilter, type ZeitListe, type ZeitStatus } from './ZeitDaten';
import './zeiten.css';

// Zeiten (/rechnung/zeiten, nur Billtoro): Stunden für Kunden mit der Uhr oder von Hand erfassen und in Rechnungen
// übernehmen. Filter in der Adresse (?monat=2026-10|alle, ?status=offen|berechnet, ?kunde=, ?person=, ?ohne=1 für
// Zeiten ohne Kunde); ?abrechnen=1 bzw. ?abrechnen=<Kunde> öffnet „Rechnung aus Zeiten“, ?neu=1 „Zeit eintragen“.
// Wer welche Zeiten sieht, entscheidet der Server (Inhaber und „Zeiten des Teams“: alle, sonst die eigenen).
// Nur Neues erfassen braucht den Tarif; ansehen, ändern, löschen, stoppen, CSV und berechnen gehen immer.

const STATUS: [ZeitStatus, string][] = [
  ['alle', 'Alle'],
  ['offen', 'Offen'],
  ['berechnet', 'Berechnet'],
];
const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const BERECHNET_FEST = 'Berechnete Zeiten ändern sich nicht. Zum Ändern bitte die Rechnung stornieren.';
// „Mo, 20.10.2026“
const tagText = (d: string) => `${WOCHENTAG[new Date(`${d}T12:00:00Z`).getUTCDay()]}, ${fmtDate(d)}`;
// „Oktober 2026“
const monatText = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
// die letzten 24 Monate, neuester zuerst
function monate(heute: string): string[] {
  const out: string[] = [];
  let y = Number(heute.slice(0, 4));
  let m = Number(heute.slice(5, 7));
  for (let i = 0; i < 24; i++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }
  return out;
}

export function Zeiten({ data, reload, meId }: { data: Overview; reload(): Promise<void>; meId: string }) {
  const toast = useToast();
  const firmId = data.firm.id;
  const tarif = data.tarif;
  const [params, setParams] = useSearchParams();
  const heuteMonat = data.today.slice(0, 7);
  const m = params.get('monat');
  const s = params.get('status');
  const filter: ZeitFilter = {
    monat: m === 'alle' || (m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m)) ? m : heuteMonat,
    status: s === 'offen' || s === 'berechnet' ? s : 'alle',
    kunde: urlId(params.get('kunde')),
    person: urlId(params.get('person')),
  };
  const nurOhneKunde = params.get('ohne') === '1';
  const abrechnen = params.get('abrechnen');
  const leistungen = useLeistungen(firmId);
  const [liste, setListe] = useState<ZeitListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  // Fenster: Zeit eintragen (zeit null) bzw. ändern
  const [dialog, setDialog] = useState<{ zeit: Zeit | null } | null>(() => (params.get('neu') === '1' ? { zeit: null } : null));
  const [weg, setWeg] = useState<Zeit | null>(null);
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState<{ id: string; number: string } | null>(null);
  // nach dem Stoppen ohne Kunde: Hinweis mit „Ändern“
  const [ohneKunde, setOhneKunde] = useState<Zeit | null>(null);
  const fehlt = fehlendeFunktion(tarif, 'zeiten');

  useEffect(() => {
    let live = true;
    api
      .get<ZeitListe>(zeitenUrl(firmId, filter))
      .then((l) => {
        if (!live) return;
        serverZeitMerken(l.jetzt);
        setListe(l);
        setFehler(null);
      })
      .catch((err) => live && setFehler(errorText(err)));
    return () => {
      live = false;
    };
    // Filter als Werte
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId, filter.monat, filter.status, filter.kunde, filter.person, version]);

  const neuLaden = () => setVersion((v) => v + 1);
  // Uhr gestartet, geändert, gestoppt oder verworfen: Liste und Kopfzeile (Chip) neu
  const uhrGeaendert = () => {
    neuLaden();
    void reload();
  };

  function setze(werte: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(werte)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  }

  async function loeschen() {
    if (!weg) return;
    setBusy(true);
    try {
      await api.del(zeitUrl(weg.id));
      toast('Zeit gelöscht');
      setWeg(null);
      setDialog(null);
      neuLaden();
    } catch (err) {
      toast(errorText(err));
      setWeg(null);
    } finally {
      setBusy(false);
    }
  }

  const alle = !!liste?.sicht.alle;
  const zeilen = useMemo(() => (liste?.zeiten ?? []).filter((z) => !nurOhneKunde || !z.kunde), [liste, nurOhneKunde]);
  // nach Tag gruppiert (neueste zuerst, wie vom Server)
  const tage = useMemo(() => {
    const g = new Map<string, Zeit[]>();
    for (const z of zeilen) g.set(z.datum, [...(g.get(z.datum) ?? []), z]);
    return [...g.entries()];
  }, [zeilen]);
  const summe = nurOhneKunde ? summeMinuten(zeilen) : (liste?.summe ?? 0);
  const offen = nurOhneKunde ? summeMinuten(zeilen.filter(istOffen)) : (liste?.offen ?? 0);
  const monatListe = monate(data.today);
  if (filter.monat !== 'alle' && !monatListe.includes(filter.monat)) monatListe.push(filter.monat);
  const personen = alle && liste?.personen && liste.personen.length > 1 ? liste.personen : null;
  const rechnen = darf(tarif, 'rechnungen');

  const eintragen = fehlt ? (
    <GesperrtKnopf tarif={tarif} fehlt={fehlt} className="btn primary" onUpgrade={() => upgradeAnbieten(tarif, 'zeiten')}>
      + Zeit eintragen
    </GesperrtKnopf>
  ) : (
    <button type="button" className="btn primary" onClick={() => setDialog({ zeit: null })}>
      + Zeit eintragen
    </button>
  );
  const starten = !liste?.uhr && <UhrStarten firmId={firmId} tarif={tarif} onUhr={uhrGeaendert} />;

  return (
    <div className="zt">
      <div className="rt-head">
        <div>
          <h1>Zeiten</h1>
          <p className="muted zt-satz-klein">Billtoro zählt hier Stunden für Ihre Rechnungen. Überstunden, Urlaub und Lohn rechnet Billtoro nicht.</p>
        </div>
        <div className="rt-head-act zt-kopf-act">
          {starten}
          {rechnen && (
            <button type="button" className="btn" onClick={() => setze({ abrechnen: '1' })}>
              Rechnung schreiben
            </button>
          )}
          {eintragen}
        </div>
      </div>

      {liste && !alle && (
        <p className="notice info">Hier sehen Sie Ihre eigenen Zeiten. Sie sieht auch der Inhaber der Firma und wer im Team das Recht „Zeiten des Teams“ hat.</p>
      )}
      {fehlt && liste && (liste.zeiten.length > 0 || liste.uhr) && (
        <p className="notice warn">Neue Zeiten erfassen Sie wieder ab dem Tarif Start. Vorhandene Zeiten können Sie weiter berechnen.</p>
      )}
      {ohneKunde && (
        <div className="notice warn zt-hinweis">
          <span>Bitte noch einen Kunden eintragen.</span>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setDialog({ zeit: ohneKunde });
              setOhneKunde(null);
            }}
          >
            Ändern
          </button>
        </div>
      )}

      {liste?.uhr && (
        <UhrKarte
          key={liste.uhr.id}
          uhr={liste.uhr}
          kunden={data.kunden}
          vorschlaege={liste.vorschlaege}
          onUhr={(u: Uhr | null) => {
            setListe((l) => (l ? { ...l, uhr: u } : l));
            if (!u) uhrGeaendert();
          }}
          onGestoppt={(z) => {
            setOhneKunde(z.kunde ? null : z);
            uhrGeaendert();
          }}
        />
      )}
      {liste && liste.laufend.length > 0 && (
        <ul className="zt-laufend" aria-label="Laufende Uhren im Team">
          {liste.laufend.map((u) => (
            <FremdeUhr key={u.id} id={u.id} name={u.person.name} onFertig={() => neuLaden()} />
          ))}
        </ul>
      )}

      <div className="zt-filter">
        <label className="field zt-f-monat">
          <span>Monat</span>
          <select value={filter.monat} onChange={(e) => setze({ monat: e.target.value === heuteMonat ? null : e.target.value })}>
            {monatListe.map((x) => (
              <option key={x} value={x}>
                {monatText(x)}
              </option>
            ))}
            <option value="alle">Alle Monate</option>
          </select>
        </label>
        <div className="field zt-f-status">
          <span>Status</span>
          <ChipRadios<ZeitStatus> label="Status" value={filter.status} options={STATUS} onChange={(v) => setze({ status: v === 'alle' ? null : v })} />
        </div>
        <div className="field zt-f-kunde">
          <span id="zt-fk">Kunde</span>
          {nurOhneKunde ? (
            <span className="zt-kunde">
              <b>Ohne Kunde</b>
              <button type="button" className="linkbtn" onClick={() => setze({ ohne: null })}>
                ändern
              </button>
            </span>
          ) : (
            <KundeSuche kunden={data.kunden} kundeId={filter.kunde} labelId="zt-fk" label="Filter Kunde" onChange={(id) => setze({ kunde: id })} />
          )}
        </div>
        {personen && (
          <label className="field zt-f-person">
            <span>Person</span>
            <select value={filter.person ?? ''} onChange={(e) => setze({ person: e.target.value || null })}>
              <option value="">Alle Personen</option>
              {personen.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {fehler && !liste ? (
        <div className="notice bad">{fehler}</div>
      ) : !liste ? (
        <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          {fehler && <div className="notice bad">{fehler}</div>}
          {liste.mehr && <p className="notice info">Es werden die neuesten 2.000 Einträge gezeigt. Bitte einen Monat oder Kunden wählen.</p>}
          {tage.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>
                {filter.status !== 'alle' || filter.kunde || filter.person || nurOhneKunde
                  ? 'Keine Zeiten zu dieser Auswahl.'
                  : filter.monat === 'alle'
                    ? 'Noch keine Zeiten.'
                    : 'Noch keine Zeiten in diesem Monat.'}
              </p>
              <div className="zt-leer-act">
                {eintragen}
                {starten}
              </div>
            </div>
          ) : (
            <div className="zt-liste">
              {tage.map(([tag, list]) => (
                <section key={tag} className="zt-tag" aria-label={tagText(tag)}>
                  <h2 className="zt-tag-h">
                    {tagText(tag)} · <span className="num">{stundenText(summeMinuten(list))}</span>
                  </h2>
                  <ul className="zt-zeilen">
                    {list.map((z) => (
                      <li key={z.id} className={`zt-zeile${z.rechnung ? ' fest' : ''}`} title={z.rechnung ? BERECHNET_FEST : undefined}>
                        <b className="num zt-dauer">{stundenText(z.minuten)}</b>
                        <span className="zt-was">
                          <span className="zt-kd">{z.kunde ? z.kunde.name : <span className="badge b-warn">Ohne Kunde</span>}</span>
                          <span className="zt-tt">{[z.taetigkeit, z.objekt].filter(Boolean).join(' · ') || <span className="muted">Ohne Tätigkeit</span>}</span>
                        </span>
                        {alle && <span className="zt-person muted">{z.person?.name ?? ''}</span>}
                        <span className="zt-chips">
                          {z.rechnung && (
                            <button type="button" className="badge b-ok zt-re" onClick={() => setPdf(z.rechnung)}>
                              Berechnet · {z.rechnung.number}
                            </button>
                          )}
                          {!z.berechnen && <span className="badge b-none">Nicht berechnen</span>}
                        </span>
                        <span className="zt-act">
                          {!z.rechnung && (
                            <>
                              <button type="button" className="linkbtn" onClick={() => setDialog({ zeit: z })}>
                                Ändern
                              </button>
                              <button type="button" className="linkbtn rt-danger" onClick={() => setWeg(z)}>
                                Löschen
                              </button>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
          <p className="zt-summe">
            Summe: <b className="num">{stundenText(summe)}</b> · davon offen <b className="num">{stundenText(offen)}</b>
          </p>
          {darf(tarif, 'export') && zeilen.length > 0 && (
            <p className="zt-csv">
              <a href={zeitenCsvUrl(firmId, filter)} download>
                Als CSV herunterladen
              </a>
            </p>
          )}
        </>
      )}

      {liste && (
        <ZeitDialog
          open={!!dialog}
          firmId={firmId}
          zeit={dialog?.zeit ?? null}
          vorgabe={{ kundeId: filter.kunde }}
          kunden={data.kunden}
          leistungen={leistungen.list}
          vorschlaege={liste.vorschlaege}
          personen={personen}
          meId={meId}
          today={data.today}
          onClose={() => {
            setDialog(null);
            if (params.get('neu')) setze({ neu: null });
          }}
          onGespeichert={(_z, weiter) => {
            if (!weiter) {
              setDialog(null);
              if (params.get('neu')) setze({ neu: null });
            }
            if (ohneKunde && _z.id === ohneKunde.id) setOhneKunde(null);
            neuLaden();
          }}
          onLoeschen={(z) => {
            setDialog(null);
            setWeg(z);
          }}
        />
      )}
      <ConfirmDialog
        open={!!weg}
        title={weg ? loeschenFrage(weg) : ''}
        confirmLabel="Ja, löschen"
        danger
        busy={busy}
        onConfirm={() => void loeschen()}
        onCancel={() => setWeg(null)}
      />
      {rechnen && (
        <AbrechnenDialog
          open={!!abrechnen}
          data={data}
          start={urlId(abrechnen)}
          alle={alle}
          onClose={() => setze({ abrechnen: null })}
          onOhneKunde={() => setze({ abrechnen: null, ohne: '1', kunde: null, status: 'offen', monat: 'alle' })}
        />
      )}
      <PdfDialog
        url={pdf ? `/api/rechnungstool/rechnung/${pdf.id}/pdf` : null}
        title={pdf ? `Rechnung ${pdf.number}` : ''}
        download={pdf ? `/api/rechnungstool/rechnung/${pdf.id}/pdf?download=1` : undefined}
        onClose={() => setPdf(null)}
      />
    </div>
  );
}
